import type { MobileNumberSetupSource } from '@/components/MobileNumberSetupForm';

type SmsCopy = {
  heading: string;
  body: string;
};

export const SMS_NOTIFICATION_COPY: Record<MobileNumberSetupSource, SmsCopy> = {
  first_message: {
    heading: 'Never miss a message',
    body: 'Busy doctors tell us they prefer a quick text. Get one the moment a new message arrives.',
  },
  // Number-capture only: this modal saves the number to the profile; the user
  // turns SMS on per-alert in the settings matrix, so the copy promises the
  // capability, not a specific text (the save button stays "Save", not "Turn
  // on texts").
  notification_settings: {
    heading: 'Add your mobile number',
    body: 'Add your number to your profile to turn on text notifications.',
  },
  // The reference wizard renders its own body inline and passes
  // `showHeader={false}`, so the shared form does not render this entry — the
  // wizard reads `heading` directly for the phone-setup step's title. `body` is
  // the fallback if the form is ever shown with a header for this source.
  // `reference_wizard` must stay a `MobileNumberSetupSource`: it routes the SMS
  // opt-in to `referencesReady` (see `getSmsEventForSource`).
  reference_wizard: {
    heading: 'Turn on text notifications',
    body: "We'll text you the moment your references are ready, so there's no need to keep checking back.",
  },
  // The inquiry-success sheet renders its own heading and body, from
  // `INQUIRY_SUCCESS_SHEET_COPY` below, and passes `showHeader={false}` — it
  // asks for push as well as SMS, so its copy varies by which channels are
  // still missing and cannot come from this SMS-only record. This entry stays
  // because the record must cover every `MobileNumberSetupSource`, and is the
  // fallback if the form is ever shown with a header for this source.
  inquiry_success: {
    heading: 'Never miss a reply',
    body: 'Busy doctors tell us they prefer a quick text. Get one as soon as an expert replies.',
  },
};

/**
 * Compact copy for the expert composer banner — the de-escalated form of the
 * `first_message` prompt above (shown once the expert dismisses that modal).
 * The message mirrors that entry's body so the modal and its banner read as one
 * nudge; keep them aligned if either changes. `actionLabel` matches the
 * canonical "Turn on texts" save label used across the SMS prompts.
 */
export const SMS_FIRST_MESSAGE_BANNER_COPY = {
  message: 'Get a text the moment a new message arrives.',
  actionLabel: 'Turn on texts',
};

/**
 * Privacy reassurance rendered beneath every SMS opt-in (see
 * `MobileNumberSetupForm`). Keep the promised scope concrete and narrow:
 * texts are transactional only. If this wording changes, keep it aligned
 * with the actual SMS sending behavior and any TCPA/consent copy.
 */
export const SMS_NOTIFICATION_REASSURANCE =
  "We'll only text you about your inquiries, never marketing, and we'll never share your number.";

/**
 * Copy for the inquiry-success sheet, which asks for SMS, push, or both.
 *
 * The body varies by that combination because the two sections gate
 * separately: a user who already has SMS sees only the push button, and a
 * text-specific pitch above it would sell a form that is not on screen.
 *
 * No variant claims anything is missed by declining. Email is on by default
 * and reaches everyone, so the honest pitch is speed rather than rescue — and
 * overstating it costs a push permission that cannot be asked for twice.
 *
 * The push section is a button alone, with no line of its own, because the
 * body already introduced it. The SMS section carries no body either; the
 * shared form supplies its own reassurance.
 */
export const INQUIRY_SUCCESS_SHEET_COPY = {
  heading: 'Never miss a reply',
  body: {
    both: 'Busy doctors tell us they prefer a quick nudge. Get a text or an alert on this device as soon as an expert replies. Email still reaches you either way.',
    sms: 'Busy doctors tell us they prefer a quick text. Get one as soon as an expert replies.',
    push: 'Prefer an alert on this device? Turn on push and we will let you know the moment a reply arrives. Email still reaches you either way.',
  },
  pushAction: 'Turn on push',
} as const;
